import React, { useState, useEffect, useMemo } from 'react';
// Import relevant Lucide Icons for better visual presentation
import { Mail, Phone, Home, ChevronLeft, ChevronRight, Search, MapPin } from 'lucide-react'; 
import Footer from '../components/Footer';

// --- Data (Ideally in a separate file) ---
const allAgents = [
    {
      name: "Steve Parker",
      email: "steve.parker@example.com",
      address: "123 Main Street, Johannesburg, 2000",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      bio: "Steve Parker is a seasoned real estate consultant with a strong focus on client satisfaction. He's committed to guiding clients through every step of the buying or selling process with ease and expertise.",
      contact: { office: "+27 11 646 5557", mobile: "+27 77 444 2225", email: "steve.parker@example.com" },
      properties: [ { id: 1, name: "Modern Family Home", price: "R2,500,000", location: "Sandton" }, { id: 2, name: "Corporate Office Space", price: "R15,000/month", location: "Rosebank" }]
    },
    {
      name: "Sarah Miller",
      email: "sarah.m@example.com",
      address: "456 Oak Avenue, Pretoria, 0001",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b0ec?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      bio: "Sarah Miller specializes in luxury residential properties. With over 8 years of experience, she has helped hundreds of clients find their dream properties and make smart investment decisions.",
      contact: { office: "+27 11 646 5558", mobile: "+27 77 444 2226", email: "sarah.m@example.com" },
      properties: [ { id: 4, name: "Executive Penthouse", price: "R8,500,000", location: "Sandton" }, { id: 5, name: "Retail Space", price: "R25,000/month", location: "Centurion" }]
    },
    {
      name: "John Doe",
      email: "john.d@example.com",
      address: "789 Pine Road, Benoni, 1500",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      bio: "John Doe brings fresh energy and innovative marketing to real estate. Specializing in industrial properties, John leverages technology and market insights to deliver exceptional results.",
      contact: { office: "+27 11 646 5559", mobile: "+27 77 444 2227", email: "john.d@example.com" },
      properties: [ { id: 7, name: "Industrial Warehouse", price: "R45,000/month", location: "Boksburg" }, { id: 8, name: "Office Complex", price: "R12,000,000", location: "Kempton Park" }]
    },
    {
      name: "Emily White",
      email: "emily.w@example.com",
      address: "321 Elm Street, Witbank, 1035",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      bio: "Emily White is known for her attention to detail and personalized service. She specializes in helping first-time buyers navigate the property market and has extensive experience in both residential sales and property management.",
      contact: { office: "+27 11 646 5560", mobile: "+27 77 444 2228", email: "emily.w@example.com" },
      properties: [ { id: 10, name: "Cozy Family Home", price: "R1,650,000", location: "Witbank" }, { id: 11, name: "Student Accommodation", price: "R8,500/month", location: "Emalahleni" }]
    }
];

// --- Sub-Component for Animated Content ---
const AgentContent = ({ agent }) => (
    // Key added here to force re-render and trigger the fade-in animation when agent changes
    <div key={agent.name} className="animate-fadeIn"> 
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-12 items-start">
            {/* Agent Image */}
            <div className="w-full h-80 sm:h-[28rem] bg-gray-200 rounded-xl overflow-hidden shadow-xl">
                <img src={agent.image} alt={agent.name} className="w-full h-full object-cover"/>
            </div>

            {/* Agent Details */}
            <div className="space-y-8">
                <div>
                    <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-2">{agent.name}</h1>
                    <p className="text-gray-600 text-lg flex items-center">
                        <MapPin className="w-5 h-5 mr-2 text-orange-500" />
                        {agent.address}
                    </p>
                </div>
                
                {/* Bio */}
                <p className="text-gray-700 leading-relaxed text-lg border-l-4 border-orange-500 pl-4">{agent.bio}</p>

                {/* Contact Info */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Details</h2>
                    <div className="bg-white rounded-xl p-5 shadow-sm border space-y-4">
                        <div className="flex items-center">
                            <Phone className="w-5 h-5 mr-3 text-orange-500 flex-shrink-0" />
                            <span className="text-gray-600 w-24">Office:</span>
                            <span className="font-semibold text-gray-900">{agent.contact.office}</span>
                        </div>
                        <div className="flex items-center">
                            <Phone className="w-5 h-5 mr-3 text-orange-500 flex-shrink-0" />
                            <span className="text-gray-600 w-24">Mobile:</span>
                            <span className="font-semibold text-gray-900">{agent.contact.mobile}</span>
                        </div>
                        <div className="flex items-center">
                            <Mail className="w-5 h-5 mr-3 text-orange-500 flex-shrink-0" />
                            <span className="text-gray-600 w-24">Email:</span>
                            <a href={`mailto:${agent.contact.email}`} className="font-semibold text-blue-600 hover:underline truncate">{agent.contact.email}</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* --- Agent's Properties Section --- */}
        <section className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Home className="w-6 h-6 mr-3 text-orange-500" />
                Current Listings ({agent.properties.length})
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {agent.properties.map(property => (
                    <div key={property.id} className="bg-white p-5 rounded-xl border border-gray-100 shadow-md transition-shadow hover:shadow-lg">
                        <h3 className="text-lg font-bold text-gray-900 truncate">{property.name}</h3>
                        <p className="text-orange-600 text-xl font-extrabold mt-1 mb-2">{property.price}</p>
                        <p className="text-sm text-gray-500 flex items-center">
                            <MapPin className="w-4 h-4 mr-1"/>
                            {property.location}
                        </p>
                        <button className="mt-3 text-sm text-blue-600 hover:underline font-medium">View Property &rarr;</button>
                    </div>
                ))}
            </div>
        </section>
    </div>
);


// --- Main Page Component ---
const AgentPage = () => {
    const [currentAgentIndex, setCurrentAgentIndex] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    // Filter agents
    const filteredAgents = useMemo(() => 
        allAgents.filter(agent =>
            agent.name.toLowerCase().includes(searchTerm.toLowerCase())
        ), 
        [searchTerm]
    );

    // Reset slider and current agent when filter changes
    useEffect(() => {
        setCurrentAgentIndex(0);
    }, [searchTerm]);

    // Auto-advance slideshow
    useEffect(() => {
        if (filteredAgents.length > 1) {
            const interval = setInterval(() => {
                setCurrentAgentIndex(prev => (prev + 1) % filteredAgents.length);
            }, 10000); // 10 seconds
            return () => clearInterval(interval);
        }
    }, [filteredAgents.length]);

    const handlePrevAgent = () => {
        setCurrentAgentIndex(prev => (prev - 1 + filteredAgents.length) % filteredAgents.length);
    };

    const handleNextAgent = () => {
        setCurrentAgentIndex(prev => (prev + 1) % filteredAgents.length);
    };

    const agent = filteredAgents[currentAgentIndex];

    return (
        <div className="min-h-screen bg-gray-50 pt-[120px] sm:pt-[88px] md:pt-[100px]"> {/* Adjusted top padding for fixed header */}
            
            {/* --- Sticky Header with Search and Navigation --- */}
            <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-20 px-4 sm:px-6">
                <div className="max-w-7xl mx-auto py-3 sm:py-4">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                        
                        {/* Search Input */}
                        <div className="relative w-full md:w-2/3 flex-grow order-2 md:order-1">
                            <input
                                type="text"
                                placeholder="Search agent name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border rounded-xl text-base focus:ring-2 focus:ring-orange-500 focus:outline-none transition-shadow"
                            />
                            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        </div>
                        
                        {/* Navigation Buttons */}
                        <div className="flex items-center gap-2 order-1 md:order-2 w-full md:w-auto justify-between md:justify-start">
                            <span className="text-sm font-medium text-gray-600 flex-shrink-0">
                                {filteredAgents.length > 0 ? `${currentAgentIndex + 1} / ${filteredAgents.length}` : '0 / 0'}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={handlePrevAgent}
                                    disabled={filteredAgents.length <= 1}
                                    className="p-2 bg-gray-200 text-gray-800 rounded-full hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    aria-label="Previous Agent"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={handleNextAgent}
                                    disabled={filteredAgents.length <= 1}
                                    className="p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    aria-label="Next Agent"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* --- Main Content --- */}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
                {agent ? (
                    <AgentContent agent={agent} />
                ) : (
                    // --- No Results Message ---
                    <div className="text-center py-20 bg-white rounded-xl shadow-lg mt-12">
                        <h2 className="text-3xl font-bold text-gray-800">No Agents Found</h2>
                        <p className="text-gray-600 mt-2">Try adjusting your search term to find an agent.</p>
                        <button 
                            onClick={() => setSearchTerm('')} 
                            className="mt-6 px-6 py-2 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition"
                        >
                            Clear Search
                        </button>
                    </div>
                )}
            </main>

            {/* --- Global CSS Styles --- */}
            <style jsx global>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
                }
            `}</style>
            
            <Footer />
        </div>
    );
};

export default AgentPage;